import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Tag,
  Plus,
  RefreshCw,
  Loader2,
  Trash2,
  Copy,
  Calendar,
  Users,
  DollarSign,
  Percent,
  TrendingUp,
  Gift,
  ChevronLeft,
  ChevronRight,
  Clock,
  CheckCircle,
  XCircle,
  Pencil,
} from 'lucide-react';
import { adminAPI } from '@/services/api';
import { toast } from 'sonner';

const CATEGORY_COLORS = {
  early_bird: 'bg-yellow-100 text-yellow-700',
  referral: 'bg-blue-100 text-blue-700',
  bulk_discount: 'bg-green-100 text-green-700',
  seasonal: 'bg-purple-100 text-purple-700',
  first_time: 'bg-pink-100 text-pink-700',
  loyalty: 'bg-orange-100 text-orange-700',
  corporate: 'bg-indigo-100 text-indigo-700',
  general: 'bg-slate-100 text-slate-700',
};

export default function AdminPromoCodesPage() {
  const [loading, setLoading] = useState(true);
  const [promoCodes, setPromoCodes] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    type: 'percentage',
    category: 'general',
    discount_value: 10,
    max_discount: '',
    min_purchase_amount: 0,
    total_uses_limit: '',
    per_user_limit: 1,
    first_time_only: false,
    corporate_only: false,
    starts_at: '',
    expires_at: '',
  });
  const [saving, setSaving] = useState(false);
  const perPage = 10;

  const fetchPromoCodes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminAPI.get('/admin/promo-codes', {
        params: { page, per_page: perPage }
      });
      setPromoCodes(response.data.promo_codes || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch promo codes:', error);
      toast.error('Failed to load promo codes');
    }
    setLoading(false);
  }, [page]);

  useEffect(() => {
    fetchPromoCodes();
  }, [fetchPromoCodes]);

  const handleCreate = async () => {
    if (!formData.code || !formData.name || !formData.starts_at) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...formData,
        total_uses_limit: formData.total_uses_limit ? parseInt(formData.total_uses_limit) : null,
        max_discount: formData.max_discount ? parseFloat(formData.max_discount) : null,
        expires_at: formData.expires_at || null,
      };
      await adminAPI.post('/admin/promo-codes', payload);
      toast.success('Promo code created');
      setCreateModalOpen(false);
      setFormData({
        code: '',
        name: '',
        description: '',
        type: 'percentage',
        category: 'general',
        discount_value: 10,
        max_discount: '',
        min_purchase_amount: 0,
        total_uses_limit: '',
        per_user_limit: 1,
        first_time_only: false,
        corporate_only: false,
        starts_at: '',
        expires_at: '',
      });
      fetchPromoCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create promo code');
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this promo code?')) return;
    try {
      await adminAPI.delete(`/admin/promo-codes/${id}`);
      toast.success('Promo code deleted');
      fetchPromoCodes();
    } catch (error) {
      toast.error('Failed to delete promo code');
    }
  };

  // Convert ISO datetime to datetime-local input value (YYYY-MM-DDTHH:mm)
  const toDatetimeLocal = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return '';
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch {
      return '';
    }
  };

  const openEditModal = (promo) => {
    setEditingPromo(promo);
    setFormData({
      code: promo.code || '',
      name: promo.name || '',
      description: promo.description || '',
      // Normalize the field (backend stores "type", some legacy records may use "discount_type")
      type: promo.type || promo.discount_type || 'percentage',
      category: promo.category || 'general',
      discount_value: promo.discount_value ?? 0,
      max_discount: promo.max_discount ?? '',
      min_purchase_amount: promo.min_purchase_amount ?? 0,
      total_uses_limit: promo.total_uses_limit ?? '',
      per_user_limit: promo.per_user_limit ?? 1,
      first_time_only: !!promo.first_time_only,
      corporate_only: !!promo.corporate_only,
      starts_at: toDatetimeLocal(promo.starts_at),
      expires_at: toDatetimeLocal(promo.expires_at),
      is_active: promo.is_active !== false,
    });
    setEditModalOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingPromo) return;
    if (!formData.name) {
      toast.error('Display name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        type: formData.type,
        category: formData.category,
        discount_value: parseFloat(formData.discount_value) || 0,
        max_discount: formData.max_discount === '' || formData.max_discount === null ? null : parseFloat(formData.max_discount),
        min_purchase_amount: parseFloat(formData.min_purchase_amount) || 0,
        total_uses_limit: formData.total_uses_limit === '' ? null : parseInt(formData.total_uses_limit),
        per_user_limit: parseInt(formData.per_user_limit) || 1,
        first_time_only: !!formData.first_time_only,
        corporate_only: !!formData.corporate_only,
        starts_at: formData.starts_at || undefined,
        expires_at: formData.expires_at || '',
        is_active: !!formData.is_active,
      };
      await adminAPI.put(`/admin/promo-codes/${editingPromo.id}`, payload);
      toast.success('Promo code updated');
      setEditModalOpen(false);
      setEditingPromo(null);
      fetchPromoCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update promo code');
    }
    setSaving(false);
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied to clipboard');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const isExpired = (promo) => {
    if (!promo.expires_at) return false;
    return new Date(promo.expires_at) < new Date();
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="space-y-6" data-testid="admin-promo-codes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-bold text-navy-700">Promo Codes & Discounts</h2>
          <p className="text-slate-500">Create and manage promotional codes and discounts</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchPromoCodes} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setCreateModalOpen(true)} size="sm">
            <Plus className="w-4 h-4 mr-2" />
            Create Promo Code
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Codes</p>
                <p className="text-3xl font-bold text-navy-700">{total}</p>
              </div>
              <div className="p-3 rounded-full bg-blue-100">
                <Tag className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Active</p>
                <p className="text-3xl font-bold text-green-600">
                  {promoCodes.filter(p => p.is_active && !isExpired(p)).length}
                </p>
              </div>
              <div className="p-3 rounded-full bg-green-100">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Redemptions</p>
                <p className="text-3xl font-bold text-purple-600">
                  {promoCodes.reduce((sum, p) => sum + (p.current_uses || 0), 0)}
                </p>
              </div>
              <div className="p-3 rounded-full bg-purple-100">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Expired</p>
                <p className="text-3xl font-bold text-red-600">
                  {promoCodes.filter(p => isExpired(p)).length}
                </p>
              </div>
              <div className="p-3 rounded-full bg-red-100">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Promo Codes Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-navy-500" />
            Promo Codes
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-navy-500" />
            </div>
          ) : promoCodes.length === 0 ? (
            <div className="text-center py-12">
              <Tag className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No promo codes yet</p>
              <Button onClick={() => setCreateModalOpen(true)} className="mt-4" size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Code
              </Button>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Discount</TableHead>
                    <TableHead>Usage</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {promoCodes.map((promo) => (
                    <TableRow key={promo.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <code className="bg-slate-100 px-2 py-1 rounded font-mono text-sm">
                            {promo.code}
                          </code>
                          <Button variant="ghost" size="sm" onClick={() => copyCode(promo.code)}>
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                        <p className="text-sm text-slate-500 mt-1">{promo.name}</p>
                      </TableCell>
                      <TableCell>
                        <Badge className={CATEGORY_COLORS[promo.category] || CATEGORY_COLORS.general}>
                          {promo.category?.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="font-semibold text-green-600">
                          {(promo.type || promo.discount_type) === 'percentage'
                            ? `${promo.discount_value}%`
                            : `$${promo.discount_value}`}
                        </span>
                        {promo.max_discount && (
                          <span className="text-slate-400 text-sm block">
                            Max: ${promo.max_discount}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">{promo.current_uses || 0}</span>
                        <span className="text-slate-400">
                          {promo.total_uses_limit ? ` / ${promo.total_uses_limit}` : ' / ∞'}
                        </span>
                      </TableCell>
                      <TableCell>{formatDate(promo.expires_at)}</TableCell>
                      <TableCell>
                        {isExpired(promo) ? (
                          <Badge className="bg-red-100 text-red-700">Expired</Badge>
                        ) : promo.is_active ? (
                          <Badge className="bg-green-100 text-green-700">Active</Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-700">Inactive</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditModal(promo)}
                          className="text-navy-600 hover:text-navy-700"
                          data-testid={`edit-promo-${promo.code}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(promo.id)}
                          className="text-red-600"
                          data-testid={`delete-promo-${promo.code}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-slate-500">Page {page} of {totalPages}</p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 1}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Create Promo Code Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Promo Code</DialogTitle>
            <DialogDescription>Set up a new promotional code</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Code *</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="SUMMER25"
                  className="mt-1 font-mono uppercase"
                />
              </div>
              <div>
                <Label>Display Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Summer Sale 25% Off"
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Get 25% off all webinars during our summer sale..."
                rows={2}
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Discount Type</Label>
                <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage</SelectItem>
                    <SelectItem value="fixed_amount">Fixed Amount</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Discount Value *</Label>
                <div className="relative mt-1">
                  <Input
                    type="number"
                    value={formData.discount_value}
                    onChange={(e) => setFormData({ ...formData, discount_value: parseFloat(e.target.value) })}
                    className="pl-8"
                  />
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                    {formData.type === 'percentage' ? '%' : '$'}
                  </span>
                </div>
              </div>
              <div>
                <Label>Max Discount</Label>
                <Input
                  type="number"
                  value={formData.max_discount}
                  onChange={(e) => setFormData({ ...formData, max_discount: e.target.value })}
                  placeholder="No limit"
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="early_bird">Early Bird</SelectItem>
                    <SelectItem value="referral">Referral</SelectItem>
                    <SelectItem value="bulk_discount">Bulk Discount</SelectItem>
                    <SelectItem value="seasonal">Seasonal</SelectItem>
                    <SelectItem value="first_time">First Time</SelectItem>
                    <SelectItem value="loyalty">Loyalty</SelectItem>
                    <SelectItem value="corporate">Corporate</SelectItem>
                    <SelectItem value="general">General</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Min Purchase Amount</Label>
                <Input
                  type="number"
                  value={formData.min_purchase_amount}
                  onChange={(e) => setFormData({ ...formData, min_purchase_amount: parseFloat(e.target.value) })}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Total Uses Limit</Label>
                <Input
                  type="number"
                  value={formData.total_uses_limit}
                  onChange={(e) => setFormData({ ...formData, total_uses_limit: e.target.value })}
                  placeholder="Unlimited"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Per User Limit</Label>
                <Input
                  type="number"
                  value={formData.per_user_limit}
                  onChange={(e) => setFormData({ ...formData, per_user_limit: parseInt(e.target.value) })}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Starts At *</Label>
                <Input
                  type="datetime-local"
                  value={formData.starts_at}
                  onChange={(e) => setFormData({ ...formData, starts_at: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Expires At</Label>
                <Input
                  type="datetime-local"
                  value={formData.expires_at}
                  onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="flex items-center gap-6 pt-2">
              <label className="flex items-center gap-2">
                <Switch
                  checked={formData.first_time_only}
                  onCheckedChange={(v) => setFormData({ ...formData, first_time_only: v })}
                />
                <span className="text-sm">First-time customers only</span>
              </label>
              <label className="flex items-center gap-2">
                <Switch
                  checked={formData.corporate_only}
                  onCheckedChange={(v) => setFormData({ ...formData, corporate_only: v })}
                />
                <span className="text-sm">Corporate accounts only</span>
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModalOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} data-testid="submit-create-promo-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Promo Code Modal */}
      <Dialog open={editModalOpen} onOpenChange={(open) => { setEditModalOpen(open); if (!open) setEditingPromo(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-promo-modal">
          <DialogHeader>
            <DialogTitle>Edit Promo Code</DialogTitle>
            <DialogDescription>Update the details of this promotional code</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Code</Label>
                <Input
                  value={formData.code}
                  disabled
                  className="mt-1 font-mono uppercase bg-slate-50"
                />
                <p className="text-xs text-slate-400 mt-1">Code cannot be changed after creation</p>
              </div>
              <div>
                <Label>Display Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Summer Sale 25% Off"
                  className="mt-1"
                  data-testid="edit-promo-name"
                />
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Discount Type</Label>
                <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                  <SelectTrigger className="mt-1" data-testid="edit-promo-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage</SelectItem>
                    <SelectItem value="fixed_amount">Fixed Amount</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Discount Value *</Label>
                <div className="relative mt-1">
                  <Input
                    type="number"
                    value={formData.discount_value}
                    onChange={(e) => setFormData({ ...formData, discount_value: parseFloat(e.target.value) })}
                    className="pl-8"
                    data-testid="edit-promo-discount-value"
                  />
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                    {formData.type === 'percentage' ? '%' : '$'}
                  </span>
                </div>
              </div>
              <div>
                <Label>Max Discount</Label>
                <Input
                  type="number"
                  value={formData.max_discount}
                  onChange={(e) => setFormData({ ...formData, max_discount: e.target.value })}
                  placeholder="No limit"
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="early_bird">Early Bird</SelectItem>
                    <SelectItem value="referral">Referral</SelectItem>
                    <SelectItem value="bulk_discount">Bulk Discount</SelectItem>
                    <SelectItem value="seasonal">Seasonal</SelectItem>
                    <SelectItem value="first_time">First Time</SelectItem>
                    <SelectItem value="loyalty">Loyalty</SelectItem>
                    <SelectItem value="corporate">Corporate</SelectItem>
                    <SelectItem value="general">General</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Min Purchase Amount</Label>
                <Input
                  type="number"
                  value={formData.min_purchase_amount}
                  onChange={(e) => setFormData({ ...formData, min_purchase_amount: parseFloat(e.target.value) })}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Total Uses Limit</Label>
                <Input
                  type="number"
                  value={formData.total_uses_limit}
                  onChange={(e) => setFormData({ ...formData, total_uses_limit: e.target.value })}
                  placeholder="Unlimited"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Per User Limit</Label>
                <Input
                  type="number"
                  value={formData.per_user_limit}
                  onChange={(e) => setFormData({ ...formData, per_user_limit: parseInt(e.target.value) })}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Starts At</Label>
                <Input
                  type="datetime-local"
                  value={formData.starts_at}
                  onChange={(e) => setFormData({ ...formData, starts_at: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Expires At</Label>
                <Input
                  type="datetime-local"
                  value={formData.expires_at}
                  onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="flex items-center gap-6 pt-2 flex-wrap">
              <label className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
                  data-testid="edit-promo-active-switch"
                />
                <span className="text-sm">Active</span>
              </label>
              <label className="flex items-center gap-2">
                <Switch
                  checked={formData.first_time_only}
                  onCheckedChange={(v) => setFormData({ ...formData, first_time_only: v })}
                />
                <span className="text-sm">First-time customers only</span>
              </label>
              <label className="flex items-center gap-2">
                <Switch
                  checked={formData.corporate_only}
                  onCheckedChange={(v) => setFormData({ ...formData, corporate_only: v })}
                />
                <span className="text-sm">Corporate accounts only</span>
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditModalOpen(false); setEditingPromo(null); }}>Cancel</Button>
            <Button onClick={handleUpdate} disabled={saving} data-testid="submit-edit-promo-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Pencil className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
